import { InternalAxiosRequestConfig } from "axios";
import $api from "./axios";
import { useOrganizationStore, initializeOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";

/**
 * Determines if an API endpoint needs organizationId parameter.
 * Only add organizationId when the organization context is actually needed:
 * - Creating new projects
 * - Fetching organization's project list
 * - User invitations
 * - Organization management endpoints
 *
 * Don't add when organization can be inferred from other context:
 * - Project-specific endpoints (organization inferred from projectId)
 * - User-specific endpoints (organization inferred from user)
 */
// TODO - validate if it's actually used apart from users/invite, GET all projects, CREATE Project
function needsOrganizationId(url: string): boolean {
  if (!url) return false;

  const needsOrgId = ["/projects", "/organizations", "/invitations", "/users/invite", "/project-templates"];

  const needsOrg = needsOrgId.some((endpoint) => url.includes(endpoint));

  const hasProjectId = /\/projects\/\d+/.test(url);

  return needsOrg && !hasProjectId;
}

/**
 * Sets up an Axios interceptor to automatically add organizationId parameter
 * for system admins who have switched to a different organization.
 *
 * This handles organization context as a cross-cutting concern at the HTTP layer,
 * but only for endpoints where organization context is actually needed.
 */
export function setupOrganizationInterceptor() {
  $api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const orgStore = useOrganizationStore.getState();
    const meStore = useMeStore.getState();

    const currentOrganizationId = orgStore.currentOrganizationId;
    const userOrgId = meStore.organizationId;
    const userRole = meStore.role;

    const shouldAddOrgId =
      isSystemAdmin(userRole) &&
      currentOrganizationId &&
      userOrgId &&
      currentOrganizationId !== userOrgId &&
      needsOrganizationId(config.url || "");

    if (shouldAddOrgId) {
      config.params = {
        ...config.params,
        organizationId: currentOrganizationId
      };
    }

    return config;
  });
}

/**
 * Initialize the organization interceptor when the app starts.
 * Should be called once during app initialization.
 */
export function initializeOrganizationHandling() {
  setupOrganizationInterceptor();

  initializeOrganizationStore();
}
